import { useCallback, useState } from "react";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { TextArea } from "@design-system/TextArea/TextArea";
import { VStack } from "@design-system/VStack/VStack";
import { HStack } from "@design-system/HStack/HStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useResendNativeInvoice } from "@api/native-invoice.api";
import styles from "./ResendInvoiceEmailModal.module.scss";

interface ResendInvoiceEmailModalProps {
    show: boolean;
    onClose: () => void;
    workspaceId: string;
    entityId: number;
    invoiceId: string;
    from: string;
    to: string;
    initialSubject: string;
    initialMessage: string;
}

export const ResendInvoiceEmailModal = ({
    show,
    onClose,
    workspaceId,
    entityId,
    invoiceId,
    from,
    to,
    initialSubject,
    initialMessage,
}: ResendInvoiceEmailModalProps) => {
    const { toast, error: toastError } = useToaster();
    const resendNativeInvoice = useResendNativeInvoice();
    const [subject, setSubject] = useState(initialSubject);
    const [message, setMessage] = useState(initialMessage);

    const isSendDisabled = !subject.trim() || resendNativeInvoice.isPending;

    const handleSend = useCallback(async () => {
        if (isSendDisabled) {
            return;
        }
        try {
            await resendNativeInvoice.mutateAsync({
                workspaceId,
                entityId,
                invoiceId,
                body: {
                    subject: subject.trim(),
                    message,
                },
            });
            toast(`Invoice sent to ${to}`);
            onClose();
        } catch {
            toastError("Failed to send invoice");
        }
    }, [
        isSendDisabled,
        resendNativeInvoice,
        workspaceId,
        entityId,
        invoiceId,
        subject,
        message,
        to,
        toast,
        toastError,
        onClose,
    ]);

    return (
        <Modal
            show={show}
            onClose={onClose}
            dialogClassName={styles.previewDialog}
        >
            <StandardModalHeader>Send invoice again</StandardModalHeader>
            <StandardModalBody>
                <VStack gap={16}>
                    <VStack gap={4}>
                        <span className={styles.fieldLabel}>From</span>
                        <span>{from}</span>
                    </VStack>
                    <VStack gap={4}>
                        <span className={styles.fieldLabel}>To</span>
                        <span>{to}</span>
                    </VStack>
                    <VStack gap={4}>
                        <span className={styles.fieldLabel}>Subject</span>
                        <input
                            className={styles.subjectInput}
                            type="text"
                            value={subject}
                            onChange={(event) => setSubject(event.target.value)}
                            maxLength={255}
                            placeholder="Add subject line"
                        />
                    </VStack>
                    <TextArea
                        size="xl"
                        rows={4}
                        className={styles.bodyTextarea}
                        value={message}
                        onChange={(event) => setMessage(event.target.value)}
                        maxLength={5000}
                        placeholder="Add a message to your customer"
                    />
                </VStack>
            </StandardModalBody>
            <StandardModalFooter>
                <HStack gap={12} justify="end">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        disabled={isSendDisabled}
                        onClick={handleSend}
                    >
                        Send
                    </Button>
                </HStack>
            </StandardModalFooter>
        </Modal>
    );
};
